import type { StoreApi } from 'zustand'
import { api, apiDelete, apiPost, apiPut } from '../lib/api'
import type { LayoutSummary, SaveTarget } from '../lib/dashboardLayouts'

export type DashboardType = 'daily' | 'monthly' | 'custom-range' | 'inventory'

/** The part of every dashboard layout store that the layout manager reads and drives. */
export interface ManagedLayoutFields {
    availableLayouts: LayoutSummary[]
    /** The personal layout on screen; null while the system default is showing */
    currentLayoutId: string | null
    currentLayoutName: string | null
    currentLayoutIsDefault: boolean
    /** When the layout on screen was last saved, and when the system default last was */
    currentLayoutUpdatedAt: string | null
    systemLayoutUpdatedAt: string | null
    storedUserId: string | null
    isEditing: boolean
    layoutVersion: number
    setEditing: (editing: boolean) => void
    hasUnsavedChanges: () => boolean
    loadLayout: (token: string, userId: string, dashboardType?: string, id?: string | null) => Promise<void>
}

export interface LayoutActions {
    /** Re-reads the user's saved layouts for this dashboard */
    refreshLayouts: (token: string) => Promise<LayoutSummary[]>
    /** Writes what's on screen to `target` — a new layout when its id is null — then opens it. Throws on failure. */
    saveToAccount: (token: string, target: SaveTarget) => Promise<void>
    /** Discards My layout, and only it, and puts the system default back on screen */
    revertToSystemDefault: (token: string, userId: string) => Promise<void>
    renameLayout: (token: string, id: string, name: string) => Promise<void>
    deleteLayout: (token: string, userId: string, id: string) => Promise<void>
    makeDefaultLayout: (token: string, id: string) => Promise<void>
    /** Counts My layout as current against a newer system default, without changing it */
    keepMyLayout: (token: string) => Promise<void>
}

export type ManagedLayoutState = ManagedLayoutFields & LayoutActions

/**
 * The save and layout-management actions, shared by all four dashboard stores so they can't drift
 * apart again (they did, and Reset and Overwrite broke on two of them). Each store supplies only how
 * to serialise what's on screen.
 */
export function createLayoutActions<S extends ManagedLayoutFields>(
    set: StoreApi<S>['setState'],
    get: StoreApi<S>['getState'],
    { dashboardType, serialize }: { dashboardType: DashboardType; serialize: (state: S) => string },
): LayoutActions {
    const patch = (p: Partial<ManagedLayoutFields>) => set(p as Partial<S>)
    const bumpVersion = () => set(s => ({ layoutVersion: s.layoutVersion + 1 }) as Partial<S>)

    const refreshLayouts = async (token: string) => {
        const res = await api(`/api/dashboard/layouts?type=${dashboardType}`, token)
        const layouts: LayoutSummary[] = res?.layouts ?? []
        patch({ availableLayouts: layouts })
        return layouts
    }
    // Once the write itself has succeeded, a list that fails to refresh doesn't make it a failure.
    const refreshAfterWrite = (token: string) =>
        refreshLayouts(token).catch(e => {
            console.warn('[layouts] list refresh failed:', e)
            return get().availableLayouts
        })

    return {
        refreshLayouts,

        saveToAccount: async (token, target) => {
            const state = get()
            const body = { layoutJson: serialize(state), dashboardType, name: target.name, isDefault: target.isDefault }
            const res = target.id
                ? await apiPut(`/api/dashboard/layout/${target.id}`, token, body)
                : await apiPost('/api/dashboard/layout', token, body)
            await refreshAfterWrite(token)
            const savedId = target.id ?? res?.id
            if (savedId && state.storedUserId) await get().loadLayout(token, state.storedUserId, dashboardType, savedId)
        },

        revertToSystemDefault: async (token, userId) => {
            // Only My layout goes: named layouts were kept on purpose, and are deleted one by one.
            const mine = (await refreshLayouts(token)).find(l => l.isDefault)
            if (mine) await apiDelete(`/api/dashboard/layout/${mine.id}`, token)
            get().setEditing(false)
            await get().loadLayout(token, userId, dashboardType, null)
            await refreshAfterWrite(token)
            bumpVersion()
        },

        renameLayout: async (token, id, name) => {
            await apiPut(`/api/dashboard/layout/${id}`, token, { name })
            await refreshAfterWrite(token)
            if (get().currentLayoutId === id) patch({ currentLayoutName: name })
        },

        deleteLayout: async (token, userId, id) => {
            await apiDelete(`/api/dashboard/layout/${id}`, token)
            await refreshAfterWrite(token)
            // The layout on screen is gone: fall back to My layout, or the system default.
            if (get().currentLayoutId === id) {
                get().setEditing(false)
                await get().loadLayout(token, userId, dashboardType, null)
                bumpVersion()
            }
        },

        makeDefaultLayout: async (token, id) => {
            await apiPut(`/api/dashboard/layout/${id}`, token, { isDefault: true })
            await refreshAfterWrite(token)
            patch({ currentLayoutIsDefault: get().currentLayoutId === id })
        },

        keepMyLayout: async (token) => {
            const { currentLayoutId, systemLayoutUpdatedAt } = get()
            if (!currentLayoutId) return
            // Re-save the stored copy exactly — not what's on screen, which can differ outside edit mode
            // (Custom Range keeps its date choices in the layout) — so it counts as current again.
            const saved = await api(`/api/dashboard/layout?type=${dashboardType}&id=${currentLayoutId}`, token)
            if (saved?.source !== 'user' || !saved.layoutJson) return
            await apiPut(`/api/dashboard/layout/${currentLayoutId}`, token, { layoutJson: saved.layoutJson })
            patch({ currentLayoutUpdatedAt: systemLayoutUpdatedAt })
        },
    }
}
